import { MouseEvent, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import { useSources } from '../context/SourcesContext';
import { Source } from '../types';
import { Button, Checkbox, FilterButton, Spinner } from '../uikit';
import { isAllSourcesSelected, sourceDisplayName } from './feedFilters';

export interface SourcesFilterProps {
  /** `null` = default/"all selected" - see feedFilters.ts's
   * `isAllSourcesSelected` for why a concrete array that happens to list
   * every active source ALSO reads as "all selected". */
  sourceIds: string[] | null;
  onChange: (sourceIds: string[] | null) => void;
  /** Computed by the page via `sourcesFilterActive` - it, not this
   * component, owns the filter state and the loaded active-source list it's
   * compared against. */
  active: boolean;
  /** Reports the loaded active-source list up to the page once fetched, so
   * feedFilters.ts's set-comparison helpers always compare against the same
   * list this popover renders checkboxes for. */
  onSourcesLoaded: (sources: Source[]) => void;
  /** COM-640: `SourcesContext#sourceScanCompletedCount`, bumped every time
   * the user's own add-source scan finishes. This component caches its
   * loaded list for the page's whole lifetime (see `LoadState`'s own
   * comment below) precisely because `fetchFilterSources` is a
   * deliberately uncached fetch - a freshly-added source needs an explicit
   * signal to invalidate that cache, since nothing else here would ever
   * notice one exists. Optional so existing/other call sites don't have to
   * thread a value through just to keep compiling. */
  reloadToken?: number;
  /** COM-647 Task 4: the "Sources Settings" footer button (Figma 2871:52928)
   * - opens the new Sources Settings page. A plain callback prop, not a
   * `useNavigate()` call in this component itself: this keeps the popover
   * free of any routing dependency (its own unit test renders it with no
   * Router ancestor - see SourcesFilter.test.tsx) and matches how every
   * other cross-cutting action here (`onChange`, `onSourcesLoaded`) is
   * already threaded down from the page rather than reached for directly. */
  onOpenSourcesSettings: () => void;
}

type LoadState = { status: 'idle' } | { status: 'loading' } | { status: 'error' } | { status: 'loaded'; sources: Source[] };

// COM-639: Figma 2871:52928 - a `FilterButton` labelled "Sources" opening a
// popover checkbox list of the project's sources that `fetchFilterSources`
// (SourcesContext.tsx) decides are worth offering. Loaded lazily - only
// once the popover is opened for the first time - and cached in this
// component's own state for the rest of the page's lifetime (`LoadState`
// never resets back to 'idle' after a successful load): `fetchFilterSources`
// is a deliberately lean, uncached fetch (SourcesContext.tsx's own comment
// on it), so re-fetching on every open would be a wasted round trip for a
// list that doesn't change during one session.
//
// Fix wave (2026-08-18, Artur): the Jira AC originally said "active sources
// only" (status 'completed'), but that predicate had no relationship to
// what the feed itself shows - `DatabaseService#getArticlesFeed` has no
// source-status predicate, so an errored/in-progress source's articles were
// already visible in the unfiltered feed with no checkbox in this dropdown
// to individually select them by. The AC is overridden, and refined once
// more since: the universe is now the three-case rule
// `shouldOfferSourceInFilter` (frontend/src/lib/filterSources.ts)
// implements - include unless a source has BOTH an error AND zero articles.
// See `fetchFilterSources`'s doc comment in SourcesContext.tsx for the full
// reasoning.
export function SourcesFilter({
  sourceIds,
  onChange,
  active,
  onSourcesLoaded,
  reloadToken,
  onOpenSourcesSettings,
}: SourcesFilterProps) {
  const { fetchFilterSources } = useSources();
  // `fetchFilterSources` is a plain function on SourcesContext's value (a
  // fresh identity every provider render, same as `fetchArticlesFeed`/
  // `getArticleSummary` in AllArticlesPage.tsx) - a ref sidesteps needing it
  // in any dependency/closure-capture list.
  const fetchFilterSourcesRef = useRef(fetchFilterSources);
  fetchFilterSourcesRef.current = fetchFilterSources;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [state, setState] = useState<LoadState>({ status: 'idle' });
  // Read inside the `reloadToken` effect below without needing `anchorEl`
  // (or `load`, which closes over `onSourcesLoaded`) in that effect's own
  // dependency array - both change identity/value far more often than the
  // effect should actually re-run.
  const anchorElRef = useRef(anchorEl);
  anchorElRef.current = anchorEl;

  const load = () => {
    setState({ status: 'loading' });
    fetchFilterSourcesRef.current().then(
      (sources) => {
        setState({ status: 'loaded', sources });
        onSourcesLoaded(sources);
      },
      // Never an unhandled rejection: `fetchFilterSources` rejects on
      // socket disconnect/reconnect or its own timeout (SourcesContext.tsx)
      // with a plain Error - shown here as a small in-popover error state
      // with its own Retry, not a page-level toast (this is a lazily-opened
      // dropdown, not part of the page's main fetch/retry machinery).
      () => setState({ status: 'error' })
    );
  };
  const loadRef = useRef(load);
  loadRef.current = load;

  // COM-640: `reloadToken` changing means the user's own add-source scan
  // just finished - this popover's cache (see `LoadState`'s own comment
  // below: it never resets to 'idle' on its own) has to be invalidated, or
  // the new source never gets a checkbox until a full page reload. Skipped
  // on the very first render (the ref starts equal to the prop) so mounting
  // with whatever token the page happens to already be on doesn't force an
  // unnecessary reload.
  const previousReloadTokenRef = useRef(reloadToken);
  useEffect(() => {
    if (reloadToken === previousReloadTokenRef.current) {
      return;
    }
    previousReloadTokenRef.current = reloadToken;
    setState({ status: 'idle' });
    // The popover is open RIGHT NOW: reload immediately rather than waiting
    // for the next `open()` call, which only calls `load()` from 'idle' on
    // a fresh click - without this, a token bump while the dropdown is
    // already open would sit on 'idle' (rendering nothing - see the
    // `state.status === 'idle'` case below) until the user closes and
    // reopens it. `onSourcesLoaded` still fires from this reload the same
    // as any other - `load()` is the one function that calls it.
    if (anchorElRef.current) {
      loadRef.current();
    }
  }, [reloadToken]);

  const open = (e: MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    if (state.status === 'idle') load();
  };
  const close = () => setAnchorEl(null);

  const sources = state.status === 'loaded' ? state.sources : [];
  const activeSourceIds = sources.map((source) => source.id);
  const allSelected = isAllSourcesSelected(sourceIds, activeSourceIds);

  const toggleSource = (id: string) => {
    // `sourceIds ?? activeSourceIds`: when the current selection is `null`
    // (default/"all selected"), the first individual toggle has to start
    // from the full active list, not an empty one - otherwise unchecking
    // one box out of "all selected" would leave every OTHER box unchecked
    // too instead of just the one the user clicked.
    const current = sourceIds ?? activeSourceIds;
    const next = current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id];
    onChange(next);
  };

  return (
    <>
      <FilterButton label="Sources" active={active} onClick={open} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // Figma 2871:52928: fixed 323px paper, 16px padding on all sides.
        // Border-radius/shadow come from the theme's own MuiPopover paper
        // override (theme.ts) - not repeated here.
        PaperProps={{ sx: { width: 323, backgroundColor: '#FFFFFF', p: 2 } }}
      >
        {state.status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Spinner sx={{ width: 24, height: 24 }} />
          </Box>
        )}

        {state.status === 'error' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 2, px: 1 }}>
            <Typography fontSize={14} color="text.secondary" textAlign="center">
              Something went wrong loading sources.
            </Typography>
            <Button variant="secondary" onClick={load}>
              Retry
            </Button>
          </Box>
        )}

        {state.status === 'loaded' && (
          // 16px gaps between the three blocks (header / divider / list).
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 12, lineHeight: '18px', fontWeight: 500, color: '#A8A8A8' }}>
                Filter Sources
              </Typography>
              {/* Kit `Button variant="text"` carries the theme's MuiButton
                  `text` styleOverride (12px/20px padding, #0E0E0E, grey
                  hover fill) - fighting all of that via sx for a bare 12px
                  link isn't worth it, so this is a plain `ButtonBase` styled
                  to spec instead. Click/label logic is unchanged. */}
              <ButtonBase
                onClick={() => onChange(allSelected ? [] : null)}
                sx={{ fontFamily: 'inherit', fontSize: 12, lineHeight: '18px', fontWeight: 500, color: '#01ADEF', p: 0 }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </ButtonBase>
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto', maxHeight: '402px', pr: 1 }}>
              {sources.map((source) => (
                <Box
                  key={source.id}
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: '#0E0E0E',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {sourceDisplayName(source)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        lineHeight: '18px',
                        fontWeight: 500,
                        color: '#5A5A5A',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {source.url}
                    </Typography>
                  </Box>
                  <Checkbox
                    checked={sourceIds === null || sourceIds.includes(source.id)}
                    onChange={() => toggleSource(source.id)}
                    sx={{ p: 0 }}
                    // The label used to come from `Checkbox`'s own `label`
                    // prop (a `FormControlLabel`); Figma's row has no
                    // visible label next to the box, so the accessible name
                    // moves to `aria-label` instead - keeps
                    // `getByRole('checkbox', { name: ... })` selectors
                    // (e2e/ui/articles/all-articles.spec.ts) working unchanged.
                    inputProps={{ 'aria-label': sourceDisplayName(source) }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* COM-647 Task 4: "Sources Settings" (Figma 2871:52928) - the ONLY
        entry point into the new Sources Settings page (SourcesSettingsPage.tsx),
        deliberately not a deep link from ComplyControl (see the design spec's
        "Out of scope"). Rendered in every popover state (loading/error/loaded),
        not just 'loaded' - it's a plain navigation link unrelated to whether
        THIS dropdown's own list fetch succeeded, so a failed load must not
        also hide the only way to reach the management page. */}
        <Divider sx={{ my: 2 }} />
        <ButtonBase
          onClick={() => {
            close();
            onOpenSourcesSettings();
          }}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            fontFamily: 'inherit',
            fontSize: 14,
            fontWeight: 500,
            color: '#0E0E0E',
          }}
        >
          Sources Settings
        </ButtonBase>
      </Popover>
    </>
  );
}
